import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { FileText, FileSpreadsheet, FileDown, Package, Target, TrendingUp, TrendingDown, CheckCircle2, Building2, CalendarOff } from 'lucide-react';
import { toast } from 'sonner';
import SAR from '../components/SAR';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Reports() {
  const [period, setPeriod] = useState('monthly');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [employeeId, setEmployeeId] = useState('all');
  const [productId, setProductId] = useState('all');
  const [employees, setEmployees] = useState([]);
  const [products, setProducts] = useState([]);
  const [report, setReport] = useState(null);
  const [tvaReport, setTvaReport] = useState(null);
  const [tvaLoading, setTvaLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/employees`).then(r => setEmployees(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/products`).then(r => setProducts(r.data)).catch(() => {});
  }, []);

  const buildParams = () => {
    const params = { period };
    if (period === 'custom') {
      if (!startDate || !endDate) {
        toast.error('يرجى تحديد نطاق التاريخ');
        return null;
      }
      params.start_date = startDate;
      params.end_date = endDate;
    }
    if (employeeId && employeeId !== 'all') params.employee_id = employeeId;
    if (productId && productId !== 'all') params.product_id = productId;
    return params;
  };

  const handleGenerate = async () => {
    const params = buildParams();
    if (!params) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/reports`, { params });
      setReport(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل توليد التقرير');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadTargets = async () => {
    const params = buildParams();
    if (!params) return;
    // strip employee/product filters — targets view always shows whole branch + all employees
    delete params.employee_id;
    delete params.product_id;
    setTvaLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/reports/targets-vs-achievements`, { params });
      setTvaReport(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل تحميل المستهدفات');
    } finally {
      setTvaLoading(false);
    }
  };

  const handleExport = async (format) => {
    const params = buildParams();
    if (!params) return;
    try {
      const response = await axios.get(`${API_URL}/api/reports/export/${format}`, {
        params,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      const ext = format.startsWith('excel') ? 'xlsx' : 'pdf';
      const tag = format.endsWith('-by-product') ? 'sales_by_product' : 'sales_report';
      a.download = `${tag}_${period}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('تم تنزيل الملف');
    } catch (error) {
      toast.error('فشل تصدير الملف');
    }
  };

  const dailyChartData = report ? Object.entries(report.by_date || {}).map(([date, amount]) => ({
    تاريخ: new Date(date).toLocaleDateString('ar-SA', { month: 'short', day: 'numeric', numberingSystem: 'latn', calendar: 'gregory' }),
    مبيعات: amount
  })) : [];

  const employeeChartData = report ? (report.by_employee || []).map(e => ({
    name: e.name, مبيعات: e.total
  })) : [];

  const productChartData = report ? (report.by_product || []).map(p => ({
    name: p.name, مبيعات: p.total
  })) : [];

  return (
    <div className="space-y-8" data-testid="reports-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-black text-primary">التقارير</h1>
        <p className="text-muted-foreground mt-1">تقارير شاملة للفرع والموظفين والمنتجات</p>
      </div>

      <Card className="p-6 border-border">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="space-y-2">
            <Label>نوع التقرير</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger data-testid="period-select" className="text-right"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">يومي</SelectItem>
                <SelectItem value="weekly">أسبوعي</SelectItem>
                <SelectItem value="monthly">شهري</SelectItem>
                <SelectItem value="quarterly">ربع سنوي</SelectItem>
                <SelectItem value="custom">نطاق مخصص</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>من تاريخ</Label>
            <Input
              data-testid="start-date-input"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              disabled={period !== 'custom'}
              className="text-right"
            />
          </div>

          <div className="space-y-2">
            <Label>إلى تاريخ</Label>
            <Input
              data-testid="end-date-input"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={period !== 'custom'}
              className="text-right"
            />
          </div>

          <div className="space-y-2">
            <Label>الموظف</Label>
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger data-testid="employee-filter-select" className="text-right"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الموظفين</SelectItem>
                {employees.map(e => (
                  <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>المنتج</Label>
            <Select value={productId} onValueChange={setProductId}>
              <SelectTrigger data-testid="product-filter-select" className="text-right"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع المنتجات</SelectItem>
                {products.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-end gap-2">
            <Button
              onClick={handleGenerate}
              data-testid="generate-report-button"
              disabled={loading}
              className="flex-1 gap-2"
            >
              <FileText className="h-4 w-4" />
              {loading ? 'جاري...' : 'توليد التقرير'}
            </Button>
            <Button
              onClick={handleLoadTargets}
              data-testid="load-targets-button"
              disabled={tvaLoading}
              variant="outline"
              className="flex-1 gap-2 border-brand_blue text-brand_blue hover:bg-brand_blue/10"
            >
              <Target className="h-4 w-4" />
              {tvaLoading ? 'جاري...' : 'المستهدفات والمحققات'}
            </Button>
          </div>
        </div>

        {report && (
          <div className="mt-4 flex flex-wrap gap-2 justify-end">
            <Button
              variant="outline"
              onClick={() => handleExport('excel')}
              data-testid="export-excel-button"
              className="gap-2"
            >
              <FileSpreadsheet className="h-4 w-4 text-success" />
              تصدير Excel
            </Button>
            <Button
              variant="outline"
              onClick={() => handleExport('pdf')}
              data-testid="export-pdf-button"
              className="gap-2"
            >
              <FileDown className="h-4 w-4 text-destructive" />
              تصدير PDF
            </Button>
            <div className="w-px bg-border mx-1 hidden sm:block" />
            <Button
              variant="outline"
              onClick={() => handleExport('excel-by-product')}
              data-testid="export-excel-by-product-button"
              className="gap-2 border-brand_blue text-brand_blue hover:bg-brand_blue/10"
            >
              <Package className="h-4 w-4" />
              <FileSpreadsheet className="h-4 w-4" />
              حسب المنتج (Excel)
            </Button>
            <Button
              variant="outline"
              onClick={() => handleExport('pdf-by-product')}
              data-testid="export-pdf-by-product-button"
              className="gap-2 border-brand_blue text-brand_blue hover:bg-brand_blue/10"
            >
              <Package className="h-4 w-4" />
              <FileDown className="h-4 w-4" />
              حسب المنتج (PDF)
            </Button>
          </div>
        )}
      </Card>

      {tvaReport && (
        <Card className="p-6 border-brand_blue/30 bg-brand_blue/5" data-testid="tva-section">
          <div className="flex items-center gap-3 mb-4">
            <Target className="h-6 w-6 text-brand_blue" />
            <div>
              <h2 className="text-xl font-black text-brand_blue">المستهدفات والمحققات</h2>
              <p className="text-xs text-foreground/60 mt-1">
                {tvaReport.period_label}
                {tvaReport.prorated && ` (مستهدف موزّع على ${tvaReport.days_in_range} يوم)`}
              </p>
            </div>
          </div>

          {/* Branch card */}
          <Card className="p-5 mb-4 bg-white" data-testid="tva-branch-card">
            <div className="flex items-center gap-2 mb-3">
              <Building2 className="h-5 w-5 text-brand_blue" />
              <h3 className="font-bold text-lg">{tvaReport.branch.name}</h3>
              {tvaReport.branch.status === 'achieved' ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-success/10 text-success font-bold text-xs">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  تم تحقيق المستهدف
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-100 text-amber-700 font-bold text-xs">
                  <TrendingDown className="h-3.5 w-3.5" />
                  أقل من المستهدف
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-foreground/60">المستهدف</p>
                <p className="text-xl font-black text-foreground font-numbers">
                  {tvaReport.branch.target.toLocaleString('en-US')} <SAR />
                </p>
              </div>
              <div>
                <p className="text-xs text-foreground/60">المحقق</p>
                <p className="text-xl font-black text-success font-numbers">
                  {tvaReport.branch.achieved.toLocaleString('en-US')} <SAR />
                </p>
              </div>
              <div>
                <p className="text-xs text-foreground/60">المتبقي</p>
                <p className={`text-xl font-black font-numbers ${tvaReport.branch.remaining > 0 ? 'text-destructive' : 'text-success'}`}>
                  {tvaReport.branch.remaining.toLocaleString('en-US')} <SAR />
                </p>
              </div>
              <div>
                <p className="text-xs text-foreground/60">النسبة</p>
                <p className={`text-xl font-black font-numbers ${tvaReport.branch.percentage >= 100 ? 'text-success' : 'text-brand_blue'}`}>
                  {tvaReport.branch.percentage}%
                </p>
              </div>
            </div>
            <div className="mt-3 h-2 bg-accent rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${tvaReport.branch.percentage >= 100 ? 'bg-success' : 'bg-brand_blue'}`}
                style={{ width: `${Math.min(tvaReport.branch.percentage, 100)}%` }}
              />
            </div>
          </Card>

          {/* Employees table */}
          <Card className="bg-white">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="tva-employees-table">
                <thead className="bg-accent/50">
                  <tr>
                    <th className="text-right py-3 px-4 text-sm font-bold">الموظف</th>
                    <th className="text-right py-3 px-4 text-sm font-bold">المستهدف</th>
                    <th className="text-right py-3 px-4 text-sm font-bold">المحقق</th>
                    <th className="text-right py-3 px-4 text-sm font-bold">المتبقي</th>
                    <th className="text-right py-3 px-4 text-sm font-bold">المعاملات</th>
                    <th className="text-right py-3 px-4 text-sm font-bold">النسبة</th>
                    <th className="text-right py-3 px-4 text-sm font-bold">الحالة</th>
                    <th className="text-right py-3 px-4 text-sm font-bold">حالة الموظف</th>
                  </tr>
                </thead>
                <tbody>
                  {tvaReport.employees.length === 0 ? (
                    <tr><td colSpan="8" className="text-center py-6 text-muted-foreground">لا يوجد موظفون</td></tr>
                  ) : tvaReport.employees.map(e => (
                    <tr key={e.id} className="border-t border-border hover:bg-accent/30">
                      <td className="py-3 px-4 font-bold">{e.name}</td>
                      <td className="py-3 px-4 font-numbers">{e.target.toLocaleString('en-US')} <SAR /></td>
                      <td className="py-3 px-4 text-success font-bold font-numbers">{e.achieved.toLocaleString('en-US')} <SAR /></td>
                      <td className={`py-3 px-4 font-numbers font-bold ${e.remaining > 0 ? 'text-destructive' : 'text-success'}`}>
                        {e.remaining.toLocaleString('en-US')} <SAR />
                      </td>
                      <td className="py-3 px-4 font-numbers">{e.transactions}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-accent rounded-full overflow-hidden min-w-[60px]">
                            <div
                              className={`h-full transition-all ${e.percentage >= 100 ? 'bg-success' : 'bg-brand_blue'}`}
                              style={{ width: `${Math.min(e.percentage, 100)}%` }}
                            />
                          </div>
                          <span className="font-numbers text-xs font-bold">{e.percentage}%</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {e.status === 'achieved' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-success/10 text-success font-bold text-xs">
                            <CheckCircle2 className="h-3 w-3" />
                            محقّق
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-100 text-amber-700 font-bold text-xs">
                            <TrendingDown className="h-3 w-3" />
                            متأخّر
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4" data-testid={`employee-attendance-${e.id}`}>
                        {e.attendance === 'on_leave' ? (
                          <div className="flex flex-col gap-1">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-orange-100 text-orange-700 font-bold text-xs w-fit">
                              <CalendarOff className="h-3 w-3" />
                              إجازة
                            </span>
                            {e.leave_period && (
                              <span className="text-[10px] text-muted-foreground font-numbers" dir="ltr">
                                {e.leave_period.start} → {e.leave_period.end}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 font-bold text-xs">
                            نشط
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </Card>
      )}

      {report && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="p-6 border-border" data-testid="report-total-sales">
              <p className="text-sm font-bold text-muted-foreground uppercase tracking-wider">إجمالي المبيعات</p>
              <p className="text-3xl sm:text-4xl font-black text-primary mt-2 font-numbers">
                {report.total_sales?.toLocaleString('en-US') || 0} <SAR />
              </p>
              <p className="text-xs text-muted-foreground mt-2">{report.period_label}</p>
            </Card>

            <Card className="p-6 border-border" data-testid="report-transactions">
              <p className="text-sm font-bold text-muted-foreground uppercase tracking-wider">عدد المعاملات</p>
              <p className="text-3xl sm:text-4xl font-black text-primary mt-2 font-numbers">
                {report.transactions_count}
              </p>
            </Card>

            <Card className="p-6 border-border" data-testid="report-average">
              <p className="text-sm font-bold text-muted-foreground uppercase tracking-wider">متوسط المعاملة</p>
              <p className="text-3xl sm:text-4xl font-black text-primary mt-2 font-numbers">
                {report.transactions_count > 0
                  ? (report.total_sales / report.transactions_count).toFixed(0)
                  : 0} <SAR />
              </p>
            </Card>
          </div>

          {dailyChartData.length > 0 && (
            <Card className="p-6 border-border" data-testid="daily-sales-chart">
              <h3 className="text-lg sm:text-xl font-bold text-primary mb-6">المبيعات حسب التاريخ</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dailyChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 32%, 91%)" />
                  <XAxis dataKey="تاريخ" tick={{ fill: 'hsl(222, 47%, 11%)', fontSize: 12 }} />
                  <YAxis tick={{ fill: 'hsl(222, 47%, 11%)', fontSize: 12 }} />
                  <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', direction: 'rtl' }} />
                  <Line type="monotone" dataKey="مبيعات" stroke="hsl(221, 83%, 53%)" strokeWidth={3} dot={{ fill: 'hsl(221, 83%, 53%)', r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-6 border-border" data-testid="employee-sales-chart">
              <h3 className="text-lg sm:text-xl font-bold text-primary mb-6">مبيعات الموظفين</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={employeeChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 32%, 91%)" />
                  <XAxis dataKey="name" tick={{ fill: 'hsl(222, 47%, 11%)', fontSize: 12 }} />
                  <YAxis tick={{ fill: 'hsl(222, 47%, 11%)', fontSize: 12 }} />
                  <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', direction: 'rtl' }} />
                  <Bar dataKey="مبيعات" fill="hsl(142, 76%, 36%)" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>

            <Card className="p-6 border-border" data-testid="product-sales-chart">
              <h3 className="text-lg sm:text-xl font-bold text-primary mb-6">مبيعات المنتجات</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={productChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214, 32%, 91%)" />
                  <XAxis dataKey="name" tick={{ fill: 'hsl(222, 47%, 11%)', fontSize: 12 }} />
                  <YAxis tick={{ fill: 'hsl(222, 47%, 11%)', fontSize: 12 }} />
                  <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', direction: 'rtl' }} />
                  <Bar dataKey="مبيعات" fill="hsl(221, 83%, 53%)" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>

          <Card className="p-6 border-border" data-testid="transactions-table">
            <h3 className="text-lg sm:text-xl font-bold text-primary mb-6">تفاصيل المعاملات ({(report.sales || []).length})</h3>
            <div className="overflow-x-auto max-h-[500px]">
              <table className="w-full">
                <thead className="bg-accent/50 sticky top-0">
                  <tr>
                    <th className="text-right py-3 px-4 text-sm font-bold">التاريخ</th>
                    <th className="text-right py-3 px-4 text-sm font-bold">الموظف</th>
                    <th className="text-right py-3 px-4 text-sm font-bold">المنتج</th>
                    <th className="text-right py-3 px-4 text-sm font-bold">الكمية</th>
                    <th className="text-right py-3 px-4 text-sm font-bold">المبلغ</th>
                    <th className="text-right py-3 px-4 text-sm font-bold">اسم العميل</th>
                    <th className="text-right py-3 px-4 text-sm font-bold">رقم التواصل</th>
                    <th className="text-right py-3 px-4 text-sm font-bold">نوع النشاط</th>
                    <th className="text-right py-3 px-4 text-sm font-bold">الدولة</th>
                    <th className="text-right py-3 px-4 text-sm font-bold">كود الخصم</th>
                  </tr>
                </thead>
                <tbody>
                  {(report.sales || []).length === 0 ? (
                    <tr><td colSpan="10" className="text-center py-8 text-muted-foreground">لا توجد معاملات</td></tr>
                  ) : (report.sales || []).map(s => (
                    <tr key={s.id} className="border-t border-border hover:bg-accent/30">
                      <td className="py-3 px-4 text-sm">{new Date(s.date).toLocaleDateString('ar-SA', { numberingSystem: 'latn', calendar: 'gregory' })}</td>
                      <td className="py-3 px-4 text-sm">{s.employee_name}</td>
                      <td className="py-3 px-4 text-sm">{s.product_name}</td>
                      <td className="py-3 px-4 text-sm font-numbers">{s.quantity || 1}</td>
                      <td className="py-3 px-4 text-sm font-numbers text-success font-bold">
                        {s.amount.toLocaleString('en-US')} <SAR />
                      </td>
                      <td className="py-3 px-4 text-sm text-foreground/70 max-w-[180px] truncate" title={s.customer_name || ''}>
                        {s.customer_name || <span className="text-foreground/30">—</span>}
                      </td>
                      <td className="py-3 px-4 text-sm text-foreground/70 font-numbers">
                        {s.customer_phone || <span className="text-foreground/30">—</span>}
                      </td>
                      <td className="py-3 px-4 text-sm text-foreground/70 max-w-[160px] truncate" title={s.activity_type || ''}>
                        {s.activity_type || <span className="text-foreground/30">—</span>}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {s.country ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 font-bold text-xs">
                            {s.country}
                          </span>
                        ) : <span className="text-foreground/30">—</span>}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {s.used_discount_code ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-brand_blue/10 text-brand_blue font-bold text-xs">
                            تم البيع بكود خصم
                          </span>
                        ) : <span className="text-foreground/30">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
